/**
 * Progress Interceptor para Axios
 * Portal ICAVI - Sistema de Barra de Progresso
 *
 * Gerencia barra de progresso visual durante requisições HTTP
 */
// ============================================================================
// Module State
// ============================================================================
/**
 * Instância do BProgress (carregada dinamicamente)
 */
let BProgress = null;
/**
 * Contador de requisições ativas
 */
let activeRequests = 0;
// ============================================================================
// Initialization
// ============================================================================
/**
 * Importação segura do BProgress usando ES6 modules
 */
try {
    import('@bprogress/core')
        .then((module) => {
        BProgress = module.BProgress || module.default || module;
    })
        .catch((error) => {
        console.warn('BProgress não pôde ser carregado em progressInterceptor:', error);
    });
}
catch (error) {
    console.warn('BProgress não pôde ser carregado em progressInterceptor:', error);
}
// ============================================================================
// Internal Functions
// ============================================================================
/**
 * Função para aplicar estilos laranja na barra de progresso
 */
const applyOrangeStyles = () => {
    setTimeout(() => {
        const progressBar = document.querySelector('#bprogress .bar');
        const progressPeg = document.querySelector('#bprogress .peg');
        const progressSpinner = document.querySelector('#bprogress .spinner-icon');
        if (progressBar) {
            progressBar.style.background = 'linear-gradient(90deg, #f97316, #ea580c)';
            progressBar.style.boxShadow = '0 2px 4px rgba(249, 115, 22, 0.3)';
        }
        if (progressPeg) {
            progressPeg.style.boxShadow = '0 0 10px #f97316, 0 0 5px #f97316';
        }
        if (progressSpinner) {
            progressSpinner.style.borderTopColor = '#f97316';
            progressSpinner.style.borderLeftColor = '#f97316';
        }
    }, 50);
};
// ============================================================================
// Interceptor Functions
// ============================================================================
/**
 * Interceptor para requisições
 * Incrementa contador e inicia barra de progresso
 * @param config - Configuração da requisição Axios
 * @returns Configuração da requisição
 */
function deveIgnorarProgresso(config) {
    return config.responseType === 'blob';
}
export const requestInterceptor = (config) => {
    if (deveIgnorarProgresso(config)) {
        return config;
    }
    // Incrementa o contador de requisições ativas
    activeRequests++;
    // Inicia o progresso se for a primeira requisição
    if (activeRequests === 1 && BProgress && typeof BProgress.start === 'function') {
        try {
            BProgress.start();
            applyOrangeStyles();
        }
        catch (error) {
            console.warn('Erro ao iniciar progresso no interceptor:', error);
        }
    }
    return config;
};
/**
 * Interceptor para respostas de sucesso
 * Decrementa contador e finaliza barra de progresso se necessário
 * @param response - Resposta Axios
 * @returns Resposta Axios
 */
export const responseSuccessInterceptor = (response) => {
    if (response.config && deveIgnorarProgresso(response.config)) {
        return response;
    }
    // Decrementa o contador de requisições ativas
    activeRequests = Math.max(0, activeRequests - 1);
    // Finaliza o progresso se não há mais requisições ativas
    if (activeRequests === 0 && BProgress && typeof BProgress.done === 'function') {
        try {
            BProgress.done();
        }
        catch (error) {
            console.warn('Erro ao finalizar progresso no interceptor:', error);
        }
    }
    return response;
};
/**
 * Interceptor para erros de resposta
 * Decrementa contador e finaliza barra de progresso se necessário
 * @param error - Erro Axios
 * @returns Promise rejeitada com o erro
 */
export const responseErrorInterceptor = (error) => {
    if (error.config && deveIgnorarProgresso(error.config)) {
        return Promise.reject(error);
    }
    // Decrementa o contador de requisições ativas
    activeRequests = Math.max(0, activeRequests - 1);
    // Finaliza o progresso se não há mais requisições ativas
    if (activeRequests === 0 && BProgress && typeof BProgress.done === 'function') {
        try {
            BProgress.done();
        }
        catch (error) {
            console.warn('Erro ao finalizar progresso no interceptor:', error);
        }
    }
    return Promise.reject(error);
};
// ============================================================================
// Setup Functions
// ============================================================================
/**
 * Função para configurar os interceptors em uma instância do axios
 * @param axiosInstance - Instância do Axios a ser configurada
 */
export const setupProgressInterceptors = (axiosInstance) => {
    // Interceptor de requisição
    axiosInstance.interceptors.request.use(requestInterceptor, (error) => {
        if (error.config && deveIgnorarProgresso(error.config)) {
            return Promise.reject(error);
        }
        activeRequests = Math.max(0, activeRequests - 1);
        if (activeRequests === 0 && BProgress && typeof BProgress.done === 'function') {
            try {
                BProgress.done();
            }
            catch (error) {
                console.warn('Erro ao finalizar progresso no interceptor:', error);
            }
        }
        return Promise.reject(error);
    });
    // Interceptor de resposta
    axiosInstance.interceptors.response.use(responseSuccessInterceptor, responseErrorInterceptor);
};
/**
 * Função para resetar o contador (útil para testes ou reinicializações)
 */
export const resetActiveRequests = () => {
    activeRequests = 0;
    if (BProgress && typeof BProgress.done === 'function') {
        try {
            BProgress.done();
        }
        catch (error) {
            console.warn('Erro ao resetar progresso:', error);
        }
    }
};
// ============================================================================
// Default Export
// ============================================================================
/**
 * Export default para compatibilidade
 */
export default {
    setupProgressInterceptors,
    resetActiveRequests,
};
